import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid2 from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Tooltip from '@mui/material/Tooltip';
import { TransitionProps } from '@mui/material/transitions';
import Typography from '@mui/material/Typography';
import { forwardRef, useEffect, useState } from 'react';
import { Team, Tournament } from '../../../models/Tournament';
import { TrophyType, getChampions } from '../../../services/TournamentService';
import { getTrophyEmoji } from '../../../utils/Icons';
import { getDomainUrl } from '../../../utils/Navigation';
import { getImageUrl } from '../../../utils/Url';

interface ChampionsProps {
  tournament: Tournament;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Champions: React.FC<ChampionsProps> = ({ tournament }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [bestTeams, setBestTeams] = useState<Team[]>([]);
  const [individualTrophies, setIndividualTrophies] = useState<{ type: TrophyType; player: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      getChampions(tournament.id)
        .then((response) => {
          setBestTeams(response.teams);
          setIndividualTrophies(response.individualTrophies);
        })
        .finally(() => setLoading(false));
    }
  }, [open, tournament.id]);

  const handleClose = () => {
    setOpen(false);
  };

  const goToDetails = (teamId: number) => {
    let url = `${window.location.href}tournament/${tournament.id}/team/${teamId}/schedule`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const bestTeamsOutsideOfPodium = bestTeams.slice(3);

  return (
    <>
      <Tooltip arrow title="Zwycięzcy" placement="left">
        <IconButton onClick={() => setOpen(true)} color="success">
          <EmojiEventsIcon fontSize="large" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} slots={{ transition: Transition }} fullScreen onClose={handleClose}>
        <DialogTitle sx={{ m: 0, p: 2 }}>Klasyfikacja turnieju: {tournament.name}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="25vh">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {bestTeams.length === 0 && (
                <Typography textAlign={'center'} variant="h6">
                  Turniej nie został jeszcze zakończony, sprawdź wyniki później.
                </Typography>
              )}
              {bestTeams && bestTeams.length > 0 && (
                <Grid2 container justifyContent={'space-evenly'} textAlign={'center'} spacing={{ xs: 2, sm: 4, md: 6 }}>
                  <Grid2 size={{ xs: 12 }}>
                    <Button variant="text" size="large" onClick={() => goToDetails(bestTeams[0].id)}>
                      <Typography variant="h4">🥇 {bestTeams[0].name}</Typography>
                      {bestTeams[0].logo && (
                        <img
                          loading="lazy"
                          width={'60'}
                          src={getImageUrl(bestTeams[0].logo)}
                          style={{ paddingLeft: '15px', verticalAlign: 'middle' }}
                          alt={`Logo zespołu ${bestTeams[0].name}`}
                        />
                      )}
                    </Button>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Button variant="text" size="large" onClick={() => goToDetails(bestTeams[1].id)}>
                      <Typography variant="h4">🥈 {bestTeams[1].name}</Typography>
                      {bestTeams[1].logo && (
                        <img
                          loading="lazy"
                          width={'60'}
                          src={getImageUrl(bestTeams[1].logo)}
                          style={{ paddingLeft: '15px', verticalAlign: 'middle' }}
                          alt={`Logo zespołu ${bestTeams[1].name}`}
                        />
                      )}
                    </Button>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <Button variant="text" size="large" onClick={() => goToDetails(bestTeams[2].id)}>
                      <Typography variant="h4">🥉 {bestTeams[2].name}</Typography>
                      {bestTeams[2].logo && (
                        <img
                          loading="lazy"
                          width={'60'}
                          src={getImageUrl(bestTeams[2].logo)}
                          style={{ paddingLeft: '15px', verticalAlign: 'middle' }}
                          alt={`Logo zespołu ${bestTeams[2].name}`}
                        />
                      )}
                    </Button>
                  </Grid2>
                  {bestTeamsOutsideOfPodium.length > 0 && (
                    <Grid2 textAlign={'left'}>
                      {bestTeamsOutsideOfPodium.map((team) => (
                        <div key={team.id}>
                          <Button variant="text" size="large" onClick={() => goToDetails(team.id)}>
                            🏅 {team.name}
                          </Button>
                        </div>
                      ))}
                    </Grid2>
                  )}
                  {individualTrophies.length > 0 && (
                    <Grid2 textAlign={'left'}>
                      <Typography variant="h6">Indywidulane wyróżnienia:</Typography>
                      {individualTrophies.map((trophy, index) => (
                        <div key={'trophy' + index}>
                          {getTrophyEmoji(trophy.type)} - {trophy.player}
                        </div>
                      ))}
                    </Grid2>
                  )}
                </Grid2>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
